<template>
  <div ref="rootEl" class="wrap">
    <slot name="trigger" :open="open" :toggle="toggle" :active-option="activeOption">
      <TriggerButton
        :active="open || active"
        :compact="compact"
        :labelled="variant === 'label'"
        :aria-expanded="open"
        aria-haspopup="menu"
        aria-label="Sort"
        title="Sort"
        @click="toggle"
      >
        <AppIcon name="sort" :size="15" />
        <span v-if="variant === 'label'" class="triggerLabel">{{ activeOption?.label || 'Sort' }}</span>
      </TriggerButton>
    </slot>

    <div v-if="open" class="menu" role="menu">
      <button
        v-for="opt in options"
        :key="opt.value"
        type="button"
        role="menuitem"
        class="menuItem"
        :class="{ active: opt.value === modelValue }"
        @click="pick(opt.value)"
      >
        {{ opt.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import TriggerButton from './TriggerButton.vue'
import { useClickOutside } from '../../composables/useClickOutside'

/**
 * A sort-options menu behind one button — the shared control every list
 * surface uses so sorting looks and behaves the same app-wide. The `label`
 * variant renders the active option's label on the trigger so the current
 * sort is never invisible; the default icon variant fits tight headers.
 */

export interface SortOption {
  value: string
  label: string
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    options: SortOption[]
    /** Accents the built-in trigger (e.g. when the sort is not the default). */
    active?: boolean
    compact?: boolean
    variant?: 'icon' | 'label'
  }>(),
  { active: false, variant: 'icon' },
)

const emit = defineEmits<{
  'update:modelValue': [string]
}>()

const open = ref(false)
const rootEl = ref<HTMLElement | null>(null)
useClickOutside(rootEl, open)

const activeOption = computed(() => props.options.find((o) => o.value === props.modelValue))

function toggle() {
  open.value = !open.value
}

function pick(value: string) {
  open.value = false
  if (value !== props.modelValue) emit('update:modelValue', value)
}
</script>

<style scoped>
.wrap { position: relative; display: inline-flex; flex-shrink: 0; }

/* Trigger shell lives in TriggerButton.vue (shared with FilterMenu). */
.triggerLabel { font-size: var(--fs-sm); font-family: inherit; white-space: nowrap; }

.menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 40;
  min-width: 160px;
  padding: var(--space-1);
  display: flex;
  flex-direction: column;
  background: var(--color-bg-surface);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md, 0 8px 24px rgba(0, 0, 0, 0.12));
}
.menuItem {
  padding: var(--space-2) var(--space-3);
  border: none;
  background: none;
  border-radius: var(--radius-sm);
  text-align: left;
  font-family: inherit;
  font-size: var(--fs-sm);
  color: var(--color-text-primary);
  cursor: pointer;
  white-space: nowrap;
}
.menuItem:hover { background: var(--color-bg-subtle); }
/* Current sort greys out — it's the state you're in, not an available choice. */
.menuItem.active { color: var(--color-text-tertiary); cursor: default; }
.menuItem.active:hover { background: none; }
</style>
